import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import type {
  PrizeHistoryItem,
  RouletteDetail,
  RoulettePrize,
  SpinResult,
} from '@/types/roulette';
import { RouletteSpinError } from '~/adapters/api-roulette/roulette-api';
import { fetchRoulettesHandler } from '~/handlers/fetch-roulettes.handler';
import { fetchRouletteDetailHandler } from '~/handlers/fetch-roulette-detail.handler';
import { postRouletteSpinHandler } from '~/handlers/post-roulette-spin.handler';
import { fetchRoulettePrizeHistoryHandler } from '~/handlers/fetch-roulette-prize-history.handler';
import { usePlayerProfileStore } from '~/stores/player-profile';
import { GAMIFICATION_SLUGS } from '~/types/player-profile';
import { tryCatch } from '~/utils/apostatudo/try-catch';
import { useModalStore } from '@/stores/apostatudo/modal';
import AnnouncementModal from '@/components/apostatudo/organisms/AnnouncementModal.vue';

// Roletinha pausada no core (Operações do site, ENG-915): o aviso da pausa
// entra no lugar do modal, e nada daqui chama a roleta.
function isSitePaused(): boolean {
  return useFeatureFlagsNew().isRoulettePaused.value;
}

// `elevated`: pode abrir de dentro do Clube Fidelidade, e tem que ficar por cima.
function openSitePausedNotice() {
  useModalStore().open(AnnouncementModal, {
    variant: 'roulette',
    elevated: true,
  });
}

// 'unavailable' = o core respondeu, e não há roleta pro segmento deste jogador
// (lista vazia). 'ineligible' = há roleta, mas não pra esta conta ainda: abaixo
// do nível CRM mínimo (`gamifications.roulette.is_eligible: false`).
//
// São separados porque a COPY é outra — "nenhuma roleta disponível pra você" fala
// de catálogo, e pra quem está fora da promoção o assunto é a promoção, não o
// catálogo. Os dois seguem sendo respostas legítimas, e nenhum oferece "tentar
// novamente" (diferente de 'error'): não há o que tentar.
export type RouletteStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'spinning'
  | 'error'
  | 'unavailable'
  | 'ineligible';

export type RouletteHistoryStatus =
  | 'idle'
  | 'loading'
  | 'loadingMore'
  | 'ready'
  | 'error';

export const useRouletteStore = defineStore('roulette', () => {
  const authStore = useAuthStore();
  const { openLogin } = useAuth();
  // Sem `useUserData()` (ENG-801): a store não precisa saber QUEM é o jogador —
  // o core resolve pelo token. Era essa dependência que amarrava a roletinha ao
  // perfil carregado e criava a janela de erro logo depois do login.
  const playerProfileStore = usePlayerProfileStore();
  // O MESMO breakpoint que pinta a tela (ENG-824): é ele que decide se a chamada se
  // declara `web_desktop` ou `web_mobile`, e é reativo, então girar o aparelho ou
  // estreitar a janela reavalia a parede sem novo request. Não é `useLayout()` porque
  // aquele registra um watcher que mexe no menu lateral e, numa store, nunca desmonta.
  const { isBlockedHere } = usePlatformGate();

  // Roletinha exige o nível CRM mínimo dela (`gamifications.roulette` do core,
  // via /players/me). Inelegível não gasta a busca da lista — cai direto no
  // aviso de nível, do mesmo jeito que o check-in e o promocode.
  //
  // Só o veredito FRESCO do core nega (`isKnownIneligibleFor`): num "não sei" —
  // perfil ainda em trânsito, requisição falhada, core offline, mapa quebrado —
  // quem responde é a lista do core, como antes. Esconder um item de menu por
  // falta de resposta é seguro (não convida ninguém); NEGAR a abertura por falta
  // de resposta não é, porque tiraria a roleta de quem tem direito a ela.
  const isKnownIneligible = computed(() =>
    playerProfileStore.isKnownIneligibleFor(GAMIFICATION_SLUGS.roulette),
  );

  // Abertura do modal global (gatilho via store, sem navegar de rota).
  const isOpen = ref(false);

  // Slug da roleta do core já resolvido no open() (antes do sheet abrir). Quando
  // setado, o init() carrega o detalhe direto por ele, sem refazer a busca da
  // lista. Evita fetch duplicado no fluxo de abertura.
  const pendingSlug = ref<string | null>(null);

  const status = ref<RouletteStatus>('idle');
  const detail = ref<RouletteDetail | null>(null);
  const errorMessage = ref<string | null>(null);

  const lastSpinResult = ref<SpinResult | null>(null);

  // Histórico de prêmios (paginado).
  const historyItems = ref<PrizeHistoryItem[]>([]);
  const historyStatus = ref<RouletteHistoryStatus>('idle');
  const historyPage = ref(0);
  const historyLastPage = ref(1);
  const canLoadMoreHistory = computed(
    () => historyPage.value < historyLastPage.value,
  );

  const prizes = computed<RoulettePrize[]>(() =>
    [...(detail.value?.prizes ?? [])].sort((a, b) => a.position - b.position),
  );

  const availableSpins = computed(() => detail.value?.availableSpins ?? 0);
  const nextSpinAvailableAt = computed(
    () => detail.value?.nextSpinAvailableAt ?? null,
  );

  const slug = computed(() => detail.value?.slug ?? '');

  // Requisito mínimo de turnover de D-1 (ENG-820). Sempre presente no payload;
  // `required: false` = não se aplica (não configurado OU dia não apurado), e
  // aí a roletinha se comporta exatamente como antes da feature.
  const turnoverRequirement = computed(
    () => detail.value?.turnoverRequirement ?? null,
  );

  // Bloqueado pelo requisito: ele se aplica e não foi batido. É estado de TELA,
  // não de erro — o jogador tem o que fazer (apostar hoje), só não agora.
  const isTurnoverBlocked = computed(
    () =>
      turnoverRequirement.value?.required === true &&
      turnoverRequirement.value.met === false,
  );

  /**
   * As três flags que o core mandou para ESTA roleta, com a precedência já aplicada
   * (ENG-824). Ausentes (contrato antigo) = liberado, e nenhuma parede monta.
   */
  const platformFlags = computed(() => detail.value?.platforms ?? null);

  /**
   * Bloqueado pela plataforma. Como o turnover, é estado de TELA e não erro — mas aqui
   * o jogador não tem o que fazer AQUI: o que ele faz é abrir noutro lugar.
   *
   * Reavalia sozinho quando a janela cruza o breakpoint, porque a plataforma do
   * `usePlatformGate` é reativa: girar o celular muda a resposta sem nova requisição,
   * que é justamente por que o core manda flags e não um veredito pronto.
   */
  const isPlatformBlocked = computed(() => isBlockedHere(platformFlags.value));

  // Progresso 0–100 para a barra. Sem mínimo (>0) não há barra a desenhar.
  const turnoverProgress = computed(() => {
    const req = turnoverRequirement.value;

    if (!req || req.minAmount <= 0) return 0;

    return Math.min(
      100,
      Math.round((req.achievedAmount / req.minAmount) * 100),
    );
  });

  // Bloqueado NÃO pode girar: o botão sai do ar. O servidor recusa de qualquer
  // forma (a regra vive lá), mas deixar o botão vivo convidaria o jogador a
  // apertar para receber um 403.
  const canSpin = computed(
    () =>
      status.value === 'ready' &&
      availableSpins.value > 0 &&
      !!slug.value &&
      !isTurnoverBlocked.value &&
      !isPlatformBlocked.value,
  );

  function reset() {
    status.value = 'idle';
    detail.value = null;
    errorMessage.value = null;
    lastSpinResult.value = null;
    pendingSlug.value = null;
    historyItems.value = [];
    historyStatus.value = 'idle';
    historyPage.value = 0;
    historyLastPage.value = 1;
  }

  async function open() {
    if (isSitePaused()) {
      openSitePausedNotice();

      return;
    }

    // Deslogado não abre o sheet da roleta: só o login. Evita o modal da
    // roletinha aparecer por cima do login em deep-links (ex.: banner
    // /mini-games/12472), já que init() exige auth de qualquer forma.
    if (!authStore.isAuthenticated) {
      openLogin();

      return;
    }

    pendingSlug.value = null;

    // Jogador que o core já disse que não participa: abre no aviso de fora da
    // promoção sem pedir a lista. O init() (no after-enter do slide-up) respeita
    // o estado e não refaz o fetch.
    if (isKnownIneligible.value) {
      status.value = 'ineligible';
      isOpen.value = true;

      return;
    }

    // Resolve a roleta ANTES de abrir o sheet, aproveitando o tempo da animação
    // de slide-up. Se a lista falhar, abre o sheet e deixa o init() tratar —
    // comportamento de antes.
    //
    // Antes isto era condicionado ao `userId` (ENG-801): sem perfil carregado a
    // lista nem era pedida. Agora a identidade é o token, que já existe aqui.
    status.value = 'loading';

    const [list] = await tryCatch(fetchRoulettesHandler());

    const first = list?.data[0];

    if (list && !first) {
      // Sem roleta no core: abre o sheet mesmo assim com o aviso de
      // indisponível (o init() no after-enter respeita e não refaz o fetch).
      status.value = 'unavailable';
    }

    // Roleta do core resolvida: guarda o slug pra o init() (no after-enter do
    // slide-up) carregar o detalhe sem refazer a busca da lista.
    if (first) {
      pendingSlug.value = first.slug;
    }

    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
  }

  function captureUnexpected(error: unknown, source: string) {
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
      { tags: { source } },
    );
  }

  // Resolve a roleta do usuário: lista do segmento → data[0]. Sem itens ⇒ indisponível.
  async function init() {
    if (isSitePaused()) return;

    if (!authStore.isAuthenticated) {
      openLogin();

      return;
    }

    // Aqui existia uma guarda de `!userId` que marcava 'error' quando o perfil
    // ainda não tinha carregado. Ela saiu com o `user_id` das rotas (ENG-801):
    // era falso negativo, não falha. O token do jogador já está pronto quando
    // `open()` confere a autenticação, e o perfil chegava depois — quem abria a
    // roletinha nessa janela via "Não foi possível carregar seus dados" sem que
    // requisição nenhuma tivesse saído.

    // O open() já resolveu que não há o que abrir pro user: o sheet abriu direto
    // no aviso — refazer o fetch aqui só causaria o flicker "Carregando..." →
    // aviso. (Nenhum dos dois estados chega aqui por outro caminho: fechar o
    // sheet passa por reset(), e retry/refresh exigem 'error'/'ready'.)
    if (status.value === 'unavailable' || status.value === 'ineligible') {
      return;
    }

    // Mesmo gate do open(), pro retry da tela de erro (`@click="store.init()"`)
    // não voltar a pedir a lista de quem o core já disse que não participa.
    if (isKnownIneligible.value) {
      status.value = 'ineligible';

      return;
    }

    // Roleta já resolvida no open() (antes do sheet abrir): carrega o detalhe
    // direto pelo slug, sem refazer a busca da lista.
    if (pendingSlug.value) {
      const targetSlug = pendingSlug.value;

      pendingSlug.value = null;
      status.value = 'loading';
      errorMessage.value = null;
      await loadDetail(targetSlug);

      return;
    }

    status.value = 'loading';
    errorMessage.value = null;

    const [list, listError] = await tryCatch(fetchRoulettesHandler());

    if (listError || !list) {
      status.value = 'error';
      errorMessage.value = 'Serviço indisponível no momento. Tente novamente.';
      captureUnexpected(listError, 'roulette.init.list');

      return;
    }

    const first = list.data[0];

    if (!first) {
      // Sem roleta no core: aviso de indisponível dentro do próprio sheet.
      // (Erro de rede/serviço acima segue na tela de erro — isso aqui é só o
      // caso legítimo de "user sem roleta".)
      status.value = 'unavailable';

      return;
    }

    await loadDetail(first.slug);
  }

  // silent: refresh de fundo (inatividade/retomada de aba). Uma falha transitória
  // não pode derrubar o modal pra tela de erro — mantém o último detalhe bom.
  async function loadDetail(
    targetSlug: string,
    options?: { silent?: boolean },
  ) {
    // Cobre também o polling do modal (countdown, inatividade, volta da aba).
    if (isSitePaused()) return;

    const [data, detailError] = await tryCatch(
      fetchRouletteDetailHandler({ slug: targetSlug }),
    );

    if (detailError || !data) {
      if (options?.silent) return;

      status.value = 'error';
      errorMessage.value = 'Serviço indisponível no momento. Tente novamente.';
      captureUnexpected(detailError, 'roulette.loadDetail');

      return;
    }

    detail.value = data;
    status.value = 'ready';
  }

  // Gira a roleta. Retorna o resultado para o componente animar a roda até a
  // position e só então abrir o popup. O sorteio é server-authoritative.
  async function spin(): Promise<SpinResult | null> {
    if (!canSpin.value) return null;

    status.value = 'spinning';
    errorMessage.value = null;

    const [result, spinError] = await tryCatch(
      postRouletteSpinHandler({ slug: slug.value }),
    );

    if (spinError || !result) {
      status.value = 'ready';

      // O adapter classifica a falha (kind); mensagem e triagem decidem aqui.
      const spinErr = spinError instanceof RouletteSpinError ? spinError : null;

      errorMessage.value =
        spinErr?.kind === 'rate_limited'
          ? 'Muitas tentativas. Aguarde um momento e tente novamente.'
          : spinErr?.kind === 'network'
            ? 'Não foi possível conectar. Tente novamente.'
            : spinErr?.message || 'Erro ao girar a roleta.';

      // Requisito de turnover: o servidor é a autoridade e acabou de dizer que a
      // tela está desatualizada (a virada do dia não faz push). Recarrega o
      // detalhe em silêncio para o painel mostrar os números certos — a mensagem
      // do core já explica o motivo enquanto isso.
      if (spinErr?.kind === 'turnover_required' && slug.value) {
        loadDetail(slug.value, { silent: true });
      }

      // Plataforma (ENG-824): quem chega aqui é o cliente que mostrou o botão porque
      // as flags dele eram velhas. A recusa traz as CERTAS — grava-as no detalhe e a
      // parede sobe na hora, sem esperar por um refetch. A mensagem de erro sai de
      // cena: o que o jogador vê é a parede, nunca um toast (APPX-24).
      // SÓ com o bloco presente. Sem esta guarda, um 403 sem `platforms` (core mais
      // velho, payload truncado, resposta de proxy) apagaria as flags boas que já
      // estavam no detalhe, zeraria a mensagem e não subiria parede nenhuma — o
      // jogador clicaria em Girar e nada aconteceria, em loop. Sem as flags, a
      // recusa segue o caminho comum e mostra a mensagem do core.
      if (spinErr?.kind === 'platform_blocked' && spinErr.platforms) {
        errorMessage.value = null;

        if (detail.value) {
          detail.value.platforms = spinErr.platforms;
        }
      }

      // 429, queda de rede, delivery_failed (giro estornado, retryável),
      // turnover_required e platform_blocked (regras de negócio) são esperados; só
      // erro da API/desconhecido interessa à triagem do Sentry.
      if (!spinErr || spinErr.kind === 'api') {
        captureUnexpected(spinError, 'roulette.spin');
      }

      return null;
    }

    lastSpinResult.value = result;

    if (detail.value) {
      detail.value.availableSpins = result.spinsRemaining;
      // Entrega síncrona: o /spin já devolve o próximo horário — atualiza o
      // countdown na hora (sem esperar o refetch do detalhe).
      detail.value.nextSpinAvailableAt = result.nextSpinAvailableAt;
    }

    return result;
  }

  // Chamado pelo componente após a animação da roda terminar.
  function settleSpin() {
    status.value = 'ready';
  }

  // Histórico de prêmios (all-time, paginado). loadHistory recarrega a 1ª página.
  async function loadHistory() {
    historyStatus.value = 'loading';

    const [page, error] = await tryCatch(
      fetchRoulettePrizeHistoryHandler({ page: 1 }),
    );

    if (error || !page) {
      historyStatus.value = 'error';
      captureUnexpected(error, 'roulette.history');

      return;
    }

    historyItems.value = page.items;
    historyPage.value = page.currentPage;
    historyLastPage.value = page.lastPage;
    historyStatus.value = 'ready';
  }

  async function loadMoreHistory() {
    if (!canLoadMoreHistory.value || historyStatus.value === 'loadingMore') {
      return;
    }

    historyStatus.value = 'loadingMore';

    const [page, error] = await tryCatch(
      fetchRoulettePrizeHistoryHandler({ page: historyPage.value + 1 }),
    );

    if (error || !page) {
      historyStatus.value = 'ready';

      return;
    }

    historyItems.value = [...historyItems.value, ...page.items];
    historyPage.value = page.currentPage;
    historyLastPage.value = page.lastPage;
    historyStatus.value = 'ready';
  }

  return {
    isOpen,
    status,
    detail,
    errorMessage,
    lastSpinResult,
    prizes,
    availableSpins,
    nextSpinAvailableAt,
    slug,
    canSpin,
    turnoverRequirement,
    isTurnoverBlocked,
    turnoverProgress,
    platformFlags,
    isPlatformBlocked,
    historyItems,
    historyStatus,
    canLoadMoreHistory,
    open,
    close,
    init,
    loadDetail,
    spin,
    settleSpin,
    loadHistory,
    loadMoreHistory,
    reset,
  };
});
